import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createProductPageApi,
  deleteProductPageApi,
  getProductPageApi,
  getProductPagesApi,
  updateProductPageApi,
} from "../api/productPagesApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { ProductPageId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateProductPageType,
  RestUpdateProductPageType,
} from "@KassaroAB/shared/types/productPage";

const productPagesQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "productPages");

const invalidateProductPageQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: productPagesQueryKey(scope) });
};

export const useGetProductPagesQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: productPagesQueryKey(scope),
    queryFn: () => getProductPagesApi(scope!),
    enabled: !!scope,
  });
};

export const useGetProductPageQuery = (
  scope: CatalogScope | null,
  productPageId: ProductPageId | null | undefined,
) => {
  return useQuery({
    queryKey: [...productPagesQueryKey(scope), productPageId],
    queryFn: () => getProductPageApi(scope!, productPageId!),
    enabled: !!scope && !!productPageId,
  });
};

export const useCreateProductPageMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productPage: RestCreateProductPageType) =>
      createProductPageApi(scope!, productPage),
    onSuccess: () => {
      invalidateProductPageQueries(queryClient, scope);
    },
  });
};

export const useUpdateProductPageMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productPageId,
      productPage,
    }: {
      productPageId: ProductPageId;
      productPage: RestUpdateProductPageType;
    }) => updateProductPageApi(scope!, productPageId, productPage),
    onSuccess: () => {
      invalidateProductPageQueries(queryClient, scope);
    },
  });
};

export const useDeleteProductPageMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productPageId: ProductPageId) =>
      deleteProductPageApi(scope!, productPageId),
    onSuccess: () => {
      invalidateProductPageQueries(queryClient, scope);
    },
  });
};
